import { createAdminRoutes } from "./admin";
import type { SchedulerDiagnosticsSource } from "./admin";
import type { AdminFunctionDetailsResponse } from "./admin-function-details.js";
import {
  createAdminAccessMiddleware,
  createAdminAccessPolicy,
  type AdminAccessContext,
  type AdminAccessPolicy,
  type AdminSurfaceAccess,
} from "./server-admin-access.js";

import type { QueryDef } from "@gencow/core";
import type {
  RuntimeOperationInventory,
  RuntimeOperationInventoryEntry,
} from "./runtime-operation-inventory.js";
import type { StorageImageTierConfig } from "./storage.js";

export {
  createAdminAccessPolicy,
  resolveAdminSurfaceAccess,
  shouldAllowAdminSurfaceHost,
} from "./server-admin-access.js";
export type { AdminAccessPolicy, AdminSurfaceAccess } from "./server-admin-access.js";

type LoggerLike = Pick<Console, "log">;

type MutationLike = {
  name: string;
  isPublic: boolean;
};

type OwnerRlsTable = {
  tableName: string;
  columnName: string;
  readPublic?: boolean;
};

type RouteRegistrar = {
  use?(
    path: string,
    handler: (context: AdminAccessContext, next: () => Promise<unknown>) => Promise<unknown>,
  ): void;
  route(path: string, handler: unknown): void;
};

export function resolveStorageTierConfig(tierConfigEnv?: string): StorageImageTierConfig | undefined {
  if (!tierConfigEnv) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(tierConfigEnv);
    return {
      autoWebp: parsed.imageAutoWebp ?? true,
      autoMaxWidth: parsed.imageAutoMaxWidth,
      resize: parsed.imageResize ?? true,
      formats: parsed.imageFormats ?? ["webp", "avif", "jpeg", "png"],
      qualityControl: parsed.imageQuality ?? true,
      cacheMaxMB: parsed.imageCacheMaxMB ?? 1024,
      transformsPerMonth: parsed.imageTransformsPerMonth ?? -1,
    };
  } catch {
    return undefined;
  }
}

export function summarizeFunctionSecurity(params: {
  queryNames: string[];
  getQueryDef(name: string): QueryDef | undefined;
  mutations: MutationLike[];
  procedureQueries?: RuntimeOperationInventoryEntry[];
  procedureMutations?: RuntimeOperationInventoryEntry[];
}) {
  const queryDefs = params.queryNames.map((name) => params.getQueryDef(name)).filter(Boolean) as QueryDef[];
  const procedureQueries = params.procedureQueries ?? [];
  const procedureMutations = params.procedureMutations ?? [];
  const publicQueries = [
    ...queryDefs.filter((queryDef) => queryDef.isPublic).map((queryDef) => queryDef.key),
    ...procedureQueries.filter((operation) => operation.allowAnonymous).map((operation) => operation.key),
  ];
  const publicMutations = [
    ...params.mutations.filter((mutation) => mutation.isPublic).map((mutation) => mutation.name),
    ...procedureMutations.filter((operation) => operation.allowAnonymous).map((operation) => operation.key),
  ];
  const mutationNames = params.mutations.map((mutation) => mutation.name);
  const totalQueries = params.queryNames.length + procedureQueries.length;
  const totalMutations = mutationNames.length + procedureMutations.length;

  return {
    mutationNames,
    publicQueries,
    publicMutations,
    protectedQueryCount: totalQueries - publicQueries.length,
    protectedMutationCount: totalMutations - publicMutations.length,
  };
}

export function logRegisteredRpcEndpoints(params: {
  queryNames: string[];
  mutationNames: string[];
  procedureQueryNames?: string[];
  procedureMutationNames?: string[];
  logger?: LoggerLike;
}): void {
  const logger = params.logger ?? console;
  logger.log("[gencow] 📡 RPC: POST /api/query, POST /api/mutation (unified legacy + procedure)");
  if (params.queryNames.length > 0) {
    logger.log(`[gencow]   legacy queries (${params.queryNames.length}):   ${params.queryNames.join(", ")}`);
  }
  if (params.mutationNames.length > 0) {
    logger.log(
      `[gencow]   legacy mutations (${params.mutationNames.length}): ${params.mutationNames.join(", ")}`,
    );
  }
  const procedureQueryNames = params.procedureQueryNames ?? [];
  const procedureMutationNames = params.procedureMutationNames ?? [];
  if (procedureQueryNames.length > 0) {
    logger.log(
      `[gencow]   procedure queries (${procedureQueryNames.length}):   ${procedureQueryNames.join(", ")}`,
    );
  }
  if (procedureMutationNames.length > 0) {
    logger.log(
      `[gencow]   procedure mutations (${procedureMutationNames.length}): ${procedureMutationNames.join(", ")}`,
    );
  }
}

export function logFunctionSecuritySummary(params: {
  summary: ReturnType<typeof summarizeFunctionSecurity>;
  logger?: LoggerLike;
}): void {
  const logger = params.logger ?? console;

  if (params.summary.publicQueries.length > 0 || params.summary.publicMutations.length > 0) {
    logger.log("");
    logger.log("[gencow] ⚠️  PUBLIC FUNCTIONS (no auth required):");
    if (params.summary.publicQueries.length > 0) {
      logger.log(`[gencow]   queries:   ${params.summary.publicQueries.join(", ")}`);
      logger.log("[gencow]   🔓 These queries return data WITHOUT authentication.");
      logger.log("[gencow]      Ensure no PII (personal data) is exposed.");
      logger.log("[gencow]      Add .limit() to prevent full table scans on large datasets.");
    }
    if (params.summary.publicMutations.length > 0) {
      logger.log(`[gencow]   mutations: ${params.summary.publicMutations.join(", ")}`);
    }
    logger.log("[gencow]   → Remove { public: true } if auth is intended.");
    logger.log("[gencow]   → 📄 gencow/SECURITY.md 참고");
  }

  logger.log(
    `[gencow] 🔒 Secure by Default: ${params.summary.protectedQueryCount} queries + ${params.summary.protectedMutationCount} mutations require auth`,
  );
}

export function logOwnerRlsSummary(params: {
  rlsTables: readonly OwnerRlsTable[];
  logger?: LoggerLike;
}): void {
  if (params.rlsTables.length === 0) {
    return;
  }

  const logger = params.logger ?? console;
  logger.log("");
  logger.log("[gencow] 🛡️  ownerRls — 2-Layer Data Isolation:");
  for (const table of params.rlsTables) {
    const mode = table.readPublic ? "read: public, CUD: owner" : "full owner isolation";
    logger.log(`[gencow]   ✓ ${table.tableName} (${table.columnName}) — ${mode}`);
  }
  logger.log("[gencow]   Layer 1: createCrud() auto-filters all queries by userId");
  logger.log("[gencow]   Layer 2: PostgreSQL RLS policies (set_config in transactions)");
}

function registerAdminSurfaceGuard(app: RouteRegistrar, policy: AdminAccessPolicy): void {
  if (policy.mode !== "local-dev" || policy.allowPublicDevHost) {
    return;
  }
  if (!app.use) {
    throw new Error("registerAdminSurfaceRoutes: local-only admin surface requires app.use()");
  }
  app.use("/_admin/*", createAdminAccessMiddleware(policy));
}

export function registerAdminSurfaceRoutes(params: {
  app: RouteRegistrar;
  db: unknown;
  rawSql: unknown;
  driver: "pglite" | "postgres";
  buildCtx: () => unknown;
  storage?: unknown;
  functionsPath?: string;
  scheduler?: SchedulerDiagnosticsSource | null;
  settingsRoutes: unknown;
  access?: AdminSurfaceAccess;
  getOperationInventory?: () => RuntimeOperationInventory;
  getFunctionDetails?: () => AdminFunctionDetailsResponse;
  createAdminRoutesImpl?: typeof createAdminRoutes;
}): void {
  const access = params.access ?? "public";
  if (access === "disabled") {
    return;
  }
  const accessPolicy = createAdminAccessPolicy(access);
  registerAdminSurfaceGuard(params.app, accessPolicy);

  const createAdminRoutesImpl = params.createAdminRoutesImpl ?? createAdminRoutes;
  const adminRoutes = createAdminRoutesImpl(
    params.db,
    params.rawSql as never,
    params.driver,
    params.buildCtx as never,
    params.storage,
    params.functionsPath,
    {
      accessPolicy,
      scheduler: params.scheduler,
      getOperationInventory: params.getOperationInventory,
      getFunctionDetails: params.getFunctionDetails,
    },
  );
  params.app.route("/_admin", adminRoutes);
  params.app.route("/_admin/settings", params.settingsRoutes);
}
